// 2026-11-03 (Keaton: "check the status of all of my APIs and services ... monthly spend / usage, budgets ... full
// transparency"): Admin › Services. The month's totals on top, then every outside service by kind: is it up (our key's
// ping and the provider's status page), what it has used, what it has cost this month and where that number comes
// from, where the month is heading, and its budget (editable here; api emails at 80% and 100%). Admins only.
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { Empty, ErrorNote, Icon, Spinner, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { CATEGORIES, INDICATOR_WORD, SOURCE_WORD, STATE_WORD, ago, parseUsd, sortServices, usd, type ServicesView, type ServiceView } from "../services";
import { toast, useStore } from "../store";

export function AdminServicesScreen() {
  const me = useStore((s) => s.me);
  const [fresh, setFresh] = useState(0);
  const { data, setData, error, loading, reload } = useLoad<ServicesView>(() => api.adminServices(fresh > 0), [fresh]);
  if (me && me.user.role !== "admin")
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-quiet">Go home</Link>}>
            This page shows the services the studio runs on.
          </Empty>
        </div>
      </Shell>
    );
  const t = data?.totals;
  const problems = data ? data.services.filter((s) => s.state === "down" || s.state === "error" || s.budget.level === "over") : [];
  return (
    <Shell>
      <div className="page svc">
        <header className="page-head costs-head">
          <div>
            <h1 className="page-title">Services</h1>
            <p className="page-lede">Every outside service the studio runs on: up or not, this month's usage and spend, and your budgets.</p>
          </div>
          <div className="svc-refresh">
            {data && <span className="muted">Checked {ago(data.checkedAt)}</span>}
            <button type="button" className="btn btn-quiet btn-small" data-action="services.refresh" disabled={loading} onClick={() => setFresh((n) => n + 1)}>
              <Icon name="redo" size={14} /> {loading ? "Checking…" : "Check now"}
            </button>
          </div>
        </header>
        {error && <ErrorNote text={`Couldn't load the services. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Checking every service" />}
        {data && t && (
          <div className={loading ? "is-dim" : ""}>
            <dl className="costs-big svc-big">
              <div>
                <dt>Spent this month</dt>
                <dd>{usd(t.monthUsd)}</dd>
                <p className="costs-markup">
                  Day {data.month.dayOfMonth} of {data.month.daysInMonth}
                </p>
              </div>
              <div>
                <dt>On pace for</dt>
                <dd>{usd(t.projectedUsd)}</dd>
                <p className="costs-markup">at this month's rate</p>
              </div>
              <div className={t.budgetUsd && t.monthUsd > t.budgetUsd ? "is-low" : ""}>
                <dt>Budgets</dt>
                <dd className="svc-plain">{t.budgetUsd ? usd(t.budgetUsd) : "None"}</dd>
                <p className={`costs-markup${t.budgetUsd && t.projectedUsd > t.budgetUsd ? " svc-warn" : ""}`}>
                  {t.budgetUsd ? `${Math.round((t.monthUsd / t.budgetUsd) * 100)}% used${t.projectedUsd > t.budgetUsd ? ", on pace to pass" : ""}` : "Set one on any service"}
                </p>
              </div>
              <div>
                <dt>Revenue (Stripe)</dt>
                <dd>{t.revenueUsd == null ? "—" : usd(t.revenueUsd)}</dd>
                <p className="costs-markup">{t.revenueUsd == null ? "Stripe isn't connected" : `${usd(t.revenueUsd - t.monthUsd)} after costs`}</p>
              </div>
            </dl>

            {!!problems.length && (
              <div className="banner banner-warn" role="alert">
                <p>
                  <strong>{problems.length === 1 ? "1 service needs a look" : `${problems.length} services need a look`}:</strong> {problems.map((s) => `${s.name} (${s.budget.level === "over" && s.state !== "down" && s.state !== "error" ? "over budget" : STATE_WORD[s.state].toLowerCase()})`).join(", ")}.
                </p>
              </div>
            )}

            {CATEGORIES.map((c) => {
              const list = sortServices(data.services.filter((s) => s.category === c.id));
              if (!list.length) return null;
              const sum = list.reduce((a, s) => a + (s.spend.monthUsd ?? 0), 0);
              return (
                <section key={c.id} className="costs-sec" aria-labelledby={`svc-${c.id}`}>
                  <h2 id={`svc-${c.id}`} className="group-title svc-group">
                    <span>{c.title}</span>
                    <span className="muted svc-group-sum">{usd(sum)} this month</span>
                  </h2>
                  <ul className="svc-grid">
                    {list.map((s) => (
                      <li key={s.id}>
                        <ServiceCard s={s} onSaved={setData} />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
            <p className="muted costs-note">
              A month's spend comes from the service's own bill where its API gives one, otherwise from what our paid calls recorded, plus the plan price you enter. Budgets email you at 80% and 100%; nothing is paused. For margins by kind of work, see{" "}
              <Link to="/admin/costs">Costs</Link>.
            </p>
          </div>
        )}
      </div>
    </Shell>
  );
}

function ServiceCard({ s, onSaved }: { s: ServiceView; onSaved: (v: ServicesView) => void }) {
  const [editing, setEditing] = useState(false);
  const sp = s.statusPage;
  const bar = s.budget.pct != null ? Math.min(100, s.budget.pct) : null;
  return (
    <article className={`svc-card svc-${s.state}${s.budget.level === "over" ? " is-over" : ""}`} aria-label={s.name}>
      <header className="svc-head">
        <span className={`svc-dot svc-dot-${s.state}`} aria-hidden />
        <h3 className="svc-name">{s.name}</h3>
        <span className={`svc-chip svc-chip-${s.state}`}>{STATE_WORD[s.state]}</span>
      </header>
      <p className="svc-role muted">{s.role}</p>
      <p className="svc-summary">{s.summary}</p>

      {(s.spend.monthUsd != null || s.budget.usd != null) && (
        <div className="svc-spend">
          <div className="svc-spend-row">
            <span className="svc-amount">{s.spend.monthUsd == null ? "—" : usd(s.spend.monthUsd)}</span>
            <span className="muted">
              this month{s.spend.source !== "none" ? ` · ${SOURCE_WORD[s.spend.source]}` : ""}
              {s.spend.source === "ledger" && s.spend.planUsd ? ` + ${usd(s.spend.planUsd)} plan` : ""}
            </span>
          </div>
          {s.spend.projectedUsd != null && s.spend.source !== "plan" && <p className="muted svc-pace">On pace for {usd(s.spend.projectedUsd)}</p>}
          {s.budget.usd != null && (
            <div className={`svc-budget svc-budget-${s.budget.level ?? "ok"}`}>
              <span className="svc-bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={bar ?? 0} aria-label={`${s.name} budget used`}>
                <span style={{ width: `${bar ?? 0}%` }} />
              </span>
              <span className="svc-budget-text">
                {s.budget.pct != null ? `${Math.round(s.budget.pct)}%` : "—"} of {usd(s.budget.usd)}
              </span>
            </div>
          )}
          {s.spend.note && <p className="muted svc-note">{s.spend.note}</p>}
        </div>
      )}

      {s.balance && s.balance.usd != null && (
        <p className="svc-balance">
          <span className="muted">{s.balance.label}</span> <strong>{usd(s.balance.usd)}</strong>
        </p>
      )}

      {!!s.usage.length && (
        <dl className="svc-usage">
          {s.usage.map((u) => (
            <div key={u.label}>
              <dt>{u.label}</dt>
              <dd>
                {u.value}
                {u.pct != null && (
                  <span className={`svc-mini${u.pct >= 100 ? " is-over" : u.pct >= 80 ? " is-near" : ""}`} aria-hidden>
                    <span style={{ width: `${Math.min(100, u.pct)}%` }} />
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <ul className="svc-checks">
        {s.ping && (
          <li className={s.ping.ok ? "" : "is-bad"}>
            <Icon name={s.ping.ok ? "check" : "alert"} size={14} /> <span>{s.ping.ok ? `Our key works${s.ping.ms != null ? ` · ${s.ping.ms} ms` : ""}` : `Our key failed: ${s.ping.error ?? "no answer"}`}</span>
          </li>
        )}
        {sp && (
          <li className={sp.indicator === "major" || sp.indicator === "critical" ? "is-bad" : sp.indicator === "minor" || sp.indicator === "maintenance" ? "is-warn" : ""}>
            <Icon name={sp.indicator === "none" ? "check" : sp.indicator === "unknown" ? "info" : "alert"} size={14} />{" "}
            <span>
              <a href={sp.url} target="_blank" rel="noopener noreferrer">
                {sp.indicator === "unknown" ? "Status page" : INDICATOR_WORD[sp.indicator]}
              </a>
              {sp.indicator !== "none" && sp.indicator !== "unknown" && sp.description ? `: ${sp.description}` : ""}
            </span>
          </li>
        )}
        {s.error && (
          <li className="is-bad">
            <Icon name="alert" size={14} /> <span>{s.error}</span>
          </li>
        )}
        {s.needs && (
          <li className="svc-needs">
            <Icon name="info" size={14} /> <span>To see more: {s.needs}</span>
          </li>
        )}
      </ul>

      {editing ? (
        <BudgetForm s={s} onCancel={() => setEditing(false)} onSaved={(v) => (onSaved(v), setEditing(false))} />
      ) : (
        <footer className="svc-foot">
          <button type="button" className="btn btn-quiet btn-small" data-action="services.budget" onClick={() => setEditing(true)}>
            <Icon name="edit" size={14} /> {s.budget.usd != null || s.spend.planUsd != null ? "Budget" : "Set a budget"}
          </button>
          <a className="btn btn-quiet btn-small" href={s.dashboardUrl} target="_blank" rel="noopener noreferrer">
            Dashboard ↗
          </a>
        </footer>
      )}
    </article>
  );
}

function BudgetForm({ s, onCancel, onSaved }: { s: ServiceView; onCancel: () => void; onSaved: (v: ServicesView) => void }) {
  const [budget, setBudget] = useState(s.budget.usd != null ? String(s.budget.usd) : "");
  const [plan, setPlan] = useState(s.spend.planUsd != null ? String(s.spend.planUsd) : "");
  const [busy, setBusy] = useState(false);
  const b = parseUsd(budget);
  const p = parseUsd(plan);
  const bad = Number.isNaN(b) || Number.isNaN(p);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (bad || busy) return;
    setBusy(true);
    try {
      onSaved(await api.saveServiceBudgets({ [s.id]: { budgetUsd: b, planUsd: p } }));
      toast(`Saved ${s.name}'s budget.`, "good");
    } catch (err) {
      toast(`Couldn't save it. ${(err as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="svc-form" onSubmit={save}>
      <label className="field">
        <span className="field-label">Monthly budget</span>
        <input inputMode="decimal" placeholder="$0" value={budget} onChange={(e) => setBudget(e.target.value)} aria-invalid={Number.isNaN(b)} data-autofocus />
        <span className="field-hint">Emails you at 80% and 100%. Empty: no budget.</span>
      </label>
      <label className="field">
        <span className="field-label">Plan price a month</span>
        <input inputMode="decimal" placeholder="$0" value={plan} onChange={(e) => setPlan(e.target.value)} aria-invalid={Number.isNaN(p)} />
        <span className="field-hint">A flat subscription its API doesn't report. Added to the month.</span>
      </label>
      <div className="svc-form-acts">
        <button type="submit" className="btn btn-primary btn-small" data-action="services.budget" disabled={bad || busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn btn-quiet btn-small" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
